"use client";

import Image from "next/image";
import { motion, type Variants } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ExternalLink, ArrowUpRight, Sparkles, Github } from "lucide-react";
import { LiquidGlassCard } from "@/components/ui/liquid-glass-card";
import { usePortfolio } from "@/components/portfolio-provider";

type Project = {
  id: number | string;
  title: string;
  description: string;
  image: string;
  category?: string;
  tags: string[];
  liveLink: string;
  githubLink: string;
  highlights: string[];
  featured?: boolean;
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
    },
  },
};

export default function Projects() {
  const { data } = usePortfolio();
  const dbProjects =
    data?.projects && data.projects.length > 0 ? data.projects : null;

  const displayProjects = dbProjects
    ? [...dbProjects]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((p, idx) => ({
        id: p.id || idx + 1,
        title: p.title,
        description: p.description,
        image: p.imageUrl,
        category: p.category,
        tags: p.tags,
        liveLink: p.liveUrl,
        githubLink: p.githubUrl,
        highlights: p.highlights,
        featured: p.featured,
      }))
    : [];

  return (
    <section
      id="projects"
      className="section-shell relative overflow-x-clip py-10 sm:py-14 md:py-18 lg:py-20"
    >
      {/* Background Liquid Ambient Light */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute left-1/3 top-1/4 h-[550px] w-[550px] rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.12)_0%,transparent_70%)]" />
        <div className="absolute -right-20 bottom-1/4 h-80 w-80 rounded-full bg-[radial-gradient(circle,rgba(201,168,76,0.08)_0%,transparent_70%)]" />
      </div>

      <div className="container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-10"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-1.5 backdrop-blur-xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.2)]">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">
              Portfolio
            </span>
          </div>
          <h2 className="section-title mb-4 font-bold">
            Selected <span className="accent-gradient-text">Projects</span>
          </h2>
          <p className="section-subtitle mx-auto max-w-2xl text-muted-foreground">
            Production-grade web systems crafted with modern full-stack
            architectures, focusing on speed, reliability, and seamless user
            experiences.
          </p>
        </motion.div>

        {/* Project Cards Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          className="grid gap-8 md:grid-cols-2"
        >
          {displayProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function ProjectCard({ project }: { project: Project }) {
  const hasGithubLink = Boolean(
    project.githubLink &&
      project.githubLink !== "#" &&
      project.githubLink.trim() !== ""
  );
  const hasImage = Boolean(project.image && project.image.trim() !== "");

  return (
    <motion.div
      variants={itemVariants as unknown as Variants}
      className="h-full"
    >
      <LiquidGlassCard
        className="h-full rounded-3xl overflow-hidden flex flex-col p-0 border-white/15"
        spotlightColor="rgba(201, 168, 76, 0.2)"
        enableTilt
        maxTiltAngle={1.0}
      >
        {/* Image Showcase Container */}
        {hasImage && (
          <div className="relative aspect-[16/9] w-full overflow-hidden border-b border-white/10 bg-black/40">
            <Image
              src={project.image}
              alt={project.title}
              className="object-cover transition-transform duration-700 group-hover:scale-105"
              fill
              loading="lazy"
            />

            {/* Liquid Glass Overlay Gradients */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#14100b] via-transparent to-transparent opacity-90" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-black/40 opacity-70" />
          </div>
        )}

        {/* Card Content */}
        <div className="p-6 sm:p-7 flex flex-col flex-1">
          {/* Tech Tags */}
          {project.tags && project.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {project.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[11px] font-semibold text-foreground/80 shadow-[inset_0_1px_0.5px_rgba(255,255,255,0.15)] transition-colors hover:border-primary/40 hover:bg-primary/15 hover:text-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Title */}
          <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-3 tracking-tight group-hover:text-primary transition-colors">
            {project.title}
          </h3>

          {/* Description */}
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
            {project.description}
          </p>

          {/* Footer Actions */}
          <div className="mt-auto border-t border-white/10 pt-5 flex items-center gap-3">
            {hasGithubLink && (
              <Button
                asChild
                variant="outline"
                className="liquid-ghost-pill rounded-full h-10 px-5 text-xs font-semibold text-foreground"
              >
                <a
                  href={project.githubLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <Github className="h-4 w-4" />
                  <span>Source</span>
                </a>
              </Button>
            )}

            {project.liveLink && project.liveLink.trim() !== "" && (
              <Button
                asChild
                className="liquid-pill-btn rounded-full h-10 px-6 text-xs font-semibold uppercase tracking-wider text-black shadow-md flex-1 sm:flex-initial"
              >
                <a
                  href={project.liveLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  <span>View Live Project</span>
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </Button>
            )}
          </div>
        </div>
      </LiquidGlassCard>
    </motion.div>
  );
}
